import { IconButtonArray, Menu, type TooltipProps } from '@neo4j-ndl/react';
import { ExpandIcon, FitToScreenIcon, Hierarchy2Icon, ShrinkIcon } from '@neo4j-ndl/react/icons';
import type { HitTargets, NVL } from '@neo4j-nvl/base';
import { ForceDirectedLayoutType, HierarchicalLayoutType } from '@neo4j-nvl/base';
import type { Node as NvlNode } from '@neo4j-nvl/base/dist/types/types/graph-element';
import type { MouseEventCallbacks } from '@neo4j-nvl/react';
import { InteractiveNvlWrapper } from '@neo4j-nvl/react';
import type { FleetManagementTypes } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { NvlControlButton } from '@nx/ui';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { DeploymentActionModals } from '../deployments/deployment-action-menu';
import { usePermissions } from '../hooks/use-permissions';
import { EntityCard } from './cards';
import { useTopologyGraph } from './topology-graph';

type DbmsHierarchyProps = {
  dbmss: FleetManagementTypes.DbmsDetails[];
  filter: string;
  containerClassName: string;
  isInDetailsView: boolean;
};

export const DbmsGraph = ({ dbmss, filter, containerClassName, isInDetailsView }: DbmsHierarchyProps) => {
  const filteredDbmss = dbmss.filter((d) => d.name.toLowerCase().includes(filter.toLowerCase()));

  const isDbmsUpdateAllowed = usePermissions().isAllowed('dbmss', 'write');

  const TOOLTIP_PLACEMENT_LEFT: TooltipProps['placement'] = 'left';
  const nvlRef = useRef<NVL | null>(null);

  const [hierarchicalLayout, setHierarchicalLayout] = useState<boolean>(false);
  const { nodes, rels, toggle, expandOrDismissAll, selectNode, selectedEntity } = useTopologyGraph({
    dbmss: filteredDbmss,
  });

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const mouseEventCallbacks: MouseEventCallbacks = {
    onNodeClick: (node: NvlNode, hitTargets: HitTargets, evt: MouseEvent) => {
      setContextMenu(null);
      selectNode(node.id);
    },
    onNodeRightClick: (node: NvlNode, hitTargets: HitTargets, evt: MouseEvent) => {
      selectNode(node.id);
      setContextMenu({ x: evt.x, y: evt.y });
    },
    onNodeDoubleClick: (node: NvlNode, hitTargets: HitTargets, evt: MouseEvent) => {
      toggle(node.id);
    },
    onCanvasClick: (evt: MouseEvent) => {
      setContextMenu(null);
      selectNode(undefined);
    },
    onCanvasRightClick: (evt: MouseEvent) => {
      setContextMenu(null);
      selectNode(undefined);
    },
    onHover: () => {
      // This callback must be defined for built-in functionality to work
    },
    onDrag: () => {
      // This callback must be defined for built-in functionality to work
    },
    onPan: () => {
      // This callback must be defined for built-in functionality to work
    },
    onZoom: () => {
      // This callback must be defined for built-in functionality to work
    },
  };

  // Context menu related:
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isUnregisterDbmsModalOpen, setIsUnregisterDbmsModalOpen] = useState(false);
  const navigate = useNavigate();

  const fitNodes = () => {
    nvlRef.current?.fit(nodes.map((n) => n.id));
  };

  useEffect(() => {
    fitNodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes.length, hierarchicalLayout]);

  return (
    <div className="border-neutral-border-weak bg-palette-neutral-bg-weak flex flex-col gap-4 overflow-y-auto rounded-2xl border">
      <div className={classNames('relative w-full', containerClassName)}>
        <InteractiveNvlWrapper
          nodes={nodes}
          rels={rels}
          mouseEventCallbacks={mouseEventCallbacks}
          layout={hierarchicalLayout ? HierarchicalLayoutType : ForceDirectedLayoutType}
          ref={nvlRef}
        />
        {isNotNullish(selectedEntity) && (
          <div className="absolute right-4 top-4">
            <EntityCard entity={selectedEntity} />
          </div>
        )}
        {isNotNullish(selectedEntity?.dbms) && (
          <DeploymentActionModals
            dbms={selectedEntity.dbms}
            isRenameModalOpen={isRenameModalOpen}
            setIsRenameModalOpen={setIsRenameModalOpen}
            isUnregisterDbmsModalOpen={isUnregisterDbmsModalOpen}
            setIsUnregisterDbmsModalOpen={setIsUnregisterDbmsModalOpen}
          />
        )}
        {isNotNullish(contextMenu) && (isNotNullish(selectedEntity?.dbms) || isNotNullish(selectedEntity?.server)) && (
          <>
            <div ref={menuRef} style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }} className="fixed" />
            <Menu isOpen anchorRef={menuRef} onClose={() => setContextMenu(null)}>
              <Menu.Items>
                <Menu.Item
                  title="Toggle relationships"
                  onClick={() => {
                    const selectedNode = nodes.find((n) => n.selected === true);
                    if (isNotNullish(selectedNode)) {
                      toggle(selectedNode.id);
                    }
                  }}
                />
                {isNotNullish(selectedEntity.dbms) && (
                  <>
                    <Menu.Divider />
                    {!isInDetailsView && (
                      <Menu.Item
                        title="Show details"
                        onClick={() => {
                          void navigate(`${selectedEntity.dbms?.dbmsId}/details/`);
                          setContextMenu(null);
                        }}
                      />
                    )}
                    <Menu.Item
                      title="Rename"
                      isDisabled={!isDbmsUpdateAllowed}
                      onClick={() => {
                        setIsRenameModalOpen(true);
                        setContextMenu(null);
                      }}
                    />
                    <Menu.Divider />
                    <Menu.Item
                      title="Unregister deployment"
                      isDisabled={!isDbmsUpdateAllowed}
                      className="text-danger-text"
                      onClick={() => setIsUnregisterDbmsModalOpen(true)}
                    />
                  </>
                )}
              </Menu.Items>
            </Menu>
          </>
        )}
        <IconButtonArray isFloating orientation="vertical" className="absolute bottom-4 right-4">
          <NvlControlButton
            active={hierarchicalLayout}
            ariaLabel="Toggle hierarchical view"
            tipContent="Toggle hierarchical view"
            onClick={() => {
              setHierarchicalLayout(!hierarchicalLayout);
            }}
            placement={TOOLTIP_PLACEMENT_LEFT}
            testid="toggle-hierarchical-view"
          >
            <Hierarchy2Icon />
          </NvlControlButton>
          <NvlControlButton
            ariaLabel="Expand all relationships"
            tipContent="Expand all relationships"
            onClick={() => {
              expandOrDismissAll(
                nodes.map((n) => n.id),
                true,
              );
            }}
            placement={TOOLTIP_PLACEMENT_LEFT}
            testid="expand-relationships"
          >
            <ExpandIcon />
          </NvlControlButton>
          <NvlControlButton
            ariaLabel="Dismiss all relationships"
            tipContent="Dismiss all relationships"
            onClick={() => {
              expandOrDismissAll(
                nodes.map((n) => n.id),
                false,
              );
            }}
            placement={TOOLTIP_PLACEMENT_LEFT}
            testid="dismiss-relationships"
          >
            <ShrinkIcon />
          </NvlControlButton>
          <NvlControlButton
            ariaLabel="Fit all nodes"
            tipContent="Fit all nodes"
            onClick={fitNodes}
            placement={TOOLTIP_PLACEMENT_LEFT}
            testid="fit-all-nodes"
          >
            <FitToScreenIcon />
          </NvlControlButton>
        </IconButtonArray>
      </div>
    </div>
  );
};
